import React from 'react';
import { Target, Lightbulb, Mail, Phone, MapPin, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";

const About = () => {
  return (
    <div className="pt-20 animate-fade-in-up">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1767380803401-86517c7402ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBkYXJrJTIwZ29sZCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2OTk4OTk4MHww&ixlib=rb-4.1.0&q=85"
            alt="About Hero"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#081c1a] via-[#081c1a]/70 to-[#081c1a]"></div>
        </div>
        <div className="relative z-10 text-center container px-6">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">Tentang <span className="text-gold">Kami</span></h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Membangun peradaban melalui sinergi dakwah dan aksi sosial yang berkelanjutan.
          </p>
        </div>
      </section>

      {/* Vision Mission */}
      <section className="py-24 bg-[#0D2B29]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="glass-card p-10 rounded-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Target size={120} />
              </div>
              <h3 className="text-3xl font-serif font-bold text-gold mb-6">Visi</h3>
              <div className="space-y-4">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Menjadi gerakan dakwah yang menghadirkan karya bernilai ilmu dan kemaslahatan, inisiatif sosial & kemasyarakatan menggabungkan kekuatan media, teknologi, dan kolaborasi untuk menyebarkan kebermanfaatan.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed italic border-l-2 border-gold/30 pl-4">
                  "To become a creative da'wah movement that unites knowledge, media, and collaboration to spread the light of Sunnah in a structured and sustainable way."
                </p>
              </div>
            </div>
            <div className="glass-card p-10 rounded-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Lightbulb size={120} />
              </div>
              <h3 className="text-3xl font-serif font-bold text-gold mb-2">Misi</h3>
              <p className="text-sm font-accent tracking-[0.2em] text-white/60 mb-8 uppercase">Faith — Focus — Create</p>

              <div className="space-y-8">
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Faith — Pondasi Keyakinan</h4>
                  <p className="text-muted-foreground mb-2 leading-relaxed">
                    Segala aktivitas berawal dari keimanan dan tauhid. Kami meyakini bahwa setiap karya harus berakar pada niat yang ikhlas dan pemahaman yang lurus.
                  </p>
                  <p className="text-sm text-gold/80 italic">
                    Every step begins with faith — rooted in sincerity and guided by truth.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Focus — Komitmen & Disiplin</h4>
                  <p className="text-muted-foreground mb-2 leading-relaxed">
                    Menjaga arah dakwah agar tetap lurus dan efektif. Kami bekerja dengan sistematis, disiplin, dan tanggung jawab, memastikan setiap karya menjadi wasilah amal jariyah.
                  </p>
                  <p className="text-sm text-gold/80 italic">
                    Focus keeps the mission aligned — channeling effort into purposeful, accountable work for lasting impact.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Create — Karya untuk Kemaslahatan</h4>
                  <p className="text-muted-foreground mb-2 leading-relaxed">
                    Mewujudkan ide menjadi karya nyata yang memperluas jangkauan dakwah, memperkuat ukhuwah, dan menghadirkan kemaslahatan bagi umat. Kreativitas bagi kami bukan sekadar estetika, tapi sarana menyebarkan ilmu, menumbuhkan iman, dan menghidupkan nilai kebaikan.
                  </p>
                  <p className="text-sm text-gold/80 italic">
                    To create for the greater good — transforming ideas into works that enlighten hearts, strengthen unity, and serve the Ummah.
                  </p>
                </div>

                <div className="pt-6 border-t border-white/10 mt-6">
                  <p className="text-center font-serif text-lg italic text-white/90">
                    "From faith comes focus, from focus comes creation, and through creation dakwah finds its way to the hearts."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team/History */}
      <section className="py-24 bg-[#081c1a]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-serif font-bold text-white mb-16">Perjalanan Kami</h2>
          <div className="max-w-4xl mx-auto border-l-2 border-gold/30 pl-8 ml-4 md:ml-auto md:mr-auto text-left space-y-12">
            {[
              { year: "November 2025", title: "Awal Mula", desc: "Thuba Project didirikan dengan kolaborasi pegiat yang peduli akan dakwah." },
              { year: "November 2025", title: "Yayasan Resmi", desc: "Resmi berbadan hukum dan memperluas jangkauan program sosial." },
              { year: "November 2025", title: "Ekspansi Digital", desc: "Meluncurkan Thuba Media untuk menjangkau audiens lebih luas." },
              { year: "2026", title: "Perbaikan dan Keberlanjutan", desc: "Fokus pada Dakwah, Media, Sosial, Pendidikan dan Pemberdayaan." }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <span className="absolute -left-[41px] top-0 w-5 h-5 bg-gold rounded-full border-4 border-[#081c1a]"></span>
                <h3 className="text-2xl font-bold text-gold mb-2">{item.year} - {item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hubungi Kami — dipindahkan dari halaman Kontak */}
      <section className="py-24 bg-[#0D2B29]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-gold/80 mb-4 bg-gold/5 px-6 py-2 rounded-full border border-gold/20 text-sm font-medium tracking-wider uppercase">
              <MessageCircle size={16} />
              Kontak
            </div>
            <h2 className="text-4xl font-serif font-bold text-white mb-4">Hubungi <span className="text-gold">Kami</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Kami siap mendengar saran, pertanyaan, atau kolaborasi kebaikan dari Anda. Silakan hubungi kami melalui saluran resmi di bawah ini.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* WhatsApp Card */}
            <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center group hover:border-green-500/50 transition-all duration-300">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MessageCircle size={40} className="text-green-500" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2">WhatsApp</h3>
              <p className="text-muted-foreground mb-8">Chat langsung dengan admin kami untuk respon cepat.</p>
              <a
                href="https://wa.me/6282310001447"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 rounded-xl shadow-lg shadow-green-900/20">
                  Chat Sekarang <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>

            {/* Email Card */}
            <div className="glass-card p-8 rounded-2xl flex flex-col items-center text-center group hover:border-gold/50 transition-all duration-300">
              <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Mail size={40} className="text-gold" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2">Email</h3>
              <p className="text-muted-foreground mb-8">Kirimkan penawaran kerjasama atau pertanyaan detail.</p>
              <a
                href="mailto:admin@thubaproject.com"
                className="w-full"
              >
                <Button className="w-full bg-white text-black hover:bg-gray-200 font-bold py-6 rounded-xl shadow-lg">
                  Kirim Email <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>

          {/* Address Info */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center justify-center gap-2 text-gold/80 mb-4 bg-gold/5 px-6 py-2 rounded-full border border-gold/10">
              <MapPin size={18} />
              <span className="text-sm font-medium tracking-wide uppercase">Lokasi Kami</span>
            </div>
            <h4 className="text-xl font-serif text-white mb-2">Thuba Project Headquarters</h4>
            <p className="text-muted-foreground">Villa Nusa Indah 3, Kelurahan Bojong Kulur, Kecamatan Gunung Putri, Kode Pos 16969</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
